import { LinkOutlined } from '@ant-design/icons'
import { Link, LinkProps } from '@tanstack/react-router'
import { Tooltip } from 'antd'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  EditableDate,
  EditableString,
} from 'features/libra/stepComponents/niiReport/NiiFieldComponents'
import { useNiiReportData } from 'features/libra/stepComponents/niiReport/NiiReportDataContext'
import {
  DescriptionItem,
  OptionalDescriptions,
} from 'features/libra/stepComponents/niiReport/OptionalDescriptions'
import { useLibraAssetModelData } from 'features/libra/stepComponents/useLibraAssetModelData'
import React, { FC, useCallback, useMemo } from 'react'
import { SEARCH_PARAM_UNIT_ID } from 'routes/(common_search_params)/-searchParams'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import styled from 'styled-components'

import { StyledCard } from './cardsStyles'

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const LinkTag: FC<{
  to: LinkProps['to']
  search?: LinkProps['search']
  ariaLabel: string
  tooltipTitle: string
}> = ({ to, search, ariaLabel, tooltipTitle }) => {
  return (
    <Tooltip title={tooltipTitle}>
      <Link
        to={to}
        search={search}
        target='_blank'
        rel='noreferrer'
        aria-label={ariaLabel}>
        <LinkOutlined />
      </Link>
    </Tooltip>
  )
}

export const RawNiiBasicInformationCard: FC<{
  analysisDateField: string
  readonly?: boolean
}> = ({ analysisDateField, readonly }) => {
  const {
    localValues,
    setLocalFieldValue,
    isLoading: isNiiLoading,
  } = useNiiReportData()
  const { unit, siteName, isLoading: isAssetLoading } = useLibraAssetModelData()

  const isLoading = isNiiLoading || isAssetLoading

  const analysisDate = unwrapFieldValue<string>(localValues[analysisDateField])

  const handleAnalysisDateChange = useCallback(
    (value: string | undefined) => {
      setLocalFieldValue(analysisDateField, wrapFieldValue(value))
    },
    [setLocalFieldValue, analysisDateField]
  )

  const unitId = unit?.id
  const siteId = unit?.tags.site

  const items = useMemo<ReadonlyArray<DescriptionItem>>(
    () => [
      [
        'Site',
        <LinkRow key='site'>
          <EditableString value={siteName} />
          {siteId && (
            <LinkTag
              to='/assets'
              search={{ [SEARCH_PARAM_SITE_ID]: siteId }}
              ariaLabel='Open site in asset database'
              tooltipTitle='Open Site'
            />
          )}
        </LinkRow>,
      ] as const,
      [
        'Asset',
        <LinkRow key='asset'>
          <EditableString value={unit?.data.name} />
          {siteId && unitId && (
            <LinkTag
              to='/assets/routing'
              search={{
                [SEARCH_PARAM_SITE_ID]: siteId,
                [SEARCH_PARAM_UNIT_ID]: unitId,
              }}
              ariaLabel='Open asset in asset database'
              tooltipTitle='Open Asset'
            />
          )}
        </LinkRow>,
      ] as const,
      [
        'Analysis Date',
        <EditableDate
          key='analysisDate'
          value={analysisDate}
          onChange={readonly ? undefined : handleAnalysisDateChange}
        />,
      ],
    ],
    [
      siteName,
      unit,
      siteId,
      unitId,
      analysisDate,
      handleAnalysisDateChange,
      readonly,
    ]
  )

  return (
    <StyledCard title='Basic Information'>
      <OptionalDescriptions items={items} isLoading={isLoading} />
    </StyledCard>
  )
}

export const NiiBasicInformationCard = React.memo(RawNiiBasicInformationCard)
